import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import {
  ChevronDown,
  Menu,
  X,
  UserCheck,
  Building2,
  ShieldCheck,
  Receipt,
  Wrench,
  Calculator,
  Scale,
  PhoneCall,
  Award,
  Heart,
  FileText,
  DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Navbar: React.FC<{ onOpenConsultation?: () => void }> = ({
  onOpenConsultation,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 20);
        ticking = false;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const serviceLinks = [
    {
      title: "Overview & Process",
      href: "/property-management",
      icon: Building2,
      desc: "Full-suite residential & commercial care",
    },
    {
      title: "Marketing",
      href: "/marketing",
      icon: FileText,
      desc: "Strategic exposure & tenant acquisition",
    },
    {
      title: "Tenant Screening",
      href: "/tenant-screening",
      icon: UserCheck,
      desc: "Background, credit & verification checks",
    },
    {
      title: "Rent Collection",
      href: "/rent-collection",
      icon: Receipt,
      desc: "Automated digital payments & disbursements",
    },
    {
      title: "Maintenance Services",
      href: "/maintenance-services",
      icon: Wrench,
      desc: "24/7 vetted vendor response & care",
    },
    {
      title: "Accounting & Financials",
      href: "/accounting",
      icon: Calculator,
      desc: "Transparent monthly reporting & owner portal",
    },
    {
      title: "Eviction Coordination",
      href: "/eviction",
      icon: Scale,
      desc: "Legal compliance & swift resolution",
    },
  ];

  const toolsLinks = [
    { title: "Rent vs Sell Calculator", href: "/rent-sell-calculator" },
    { title: "Vacancy Loss Calculator", href: "/vacancy-loss-calculator" },
    { title: "ROI Calculator", href: "/roi-calculator" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-md py-1"
          : "bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm py-1 border-b border-slate-200/50"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Logo />

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
            <Link
              to="/"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                location.pathname === "/"
                  ? "text-primary font-semibold bg-primary/10"
                  : "text-slate-700 hover:text-primary hover:bg-slate-100 dark:text-slate-200"
              }`}
            >
              Home
            </Link>

            <Link
              to="/about"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                location.pathname === "/about"
                  ? "text-primary font-semibold bg-primary/10"
                  : "text-slate-700 hover:text-primary hover:bg-slate-100 dark:text-slate-200"
              }`}
            >
              About
            </Link>

            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-700 hover:text-primary hover:bg-slate-100 dark:text-slate-200 rounded-lg transition-colors focus:outline-none">
                Services <ChevronDown className="w-4 h-4 opacity-70" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-80 p-2 shadow-xl border-slate-200/80"
              >
                {serviceLinks.map((s) => {
                  const Icon = s.icon;
                  return (
                    <DropdownMenuItem
                      key={s.href}
                      asChild
                      className="cursor-pointer p-2.5 rounded-lg focus:bg-slate-100 dark:focus:bg-slate-800"
                    >
                      <Link to={s.href} className="flex items-start gap-3">
                        <div className="p-2 rounded-md bg-primary/10 text-primary mt-0.5">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-900 dark:text-white">
                            {s.title}
                          </div>
                          <div className="text-xs text-slate-500">{s.desc}</div>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/pricing"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                location.pathname === "/pricing"
                  ? "text-primary font-semibold bg-primary/10"
                  : "text-slate-700 hover:text-primary hover:bg-slate-100 dark:text-slate-200"
              }`}
            >
              Pricing
            </Link>

            {/* Calculators & Tools */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-700 hover:text-primary hover:bg-slate-100 dark:text-slate-200 rounded-lg transition-colors focus:outline-none">
                Calculators <ChevronDown className="w-4 h-4 opacity-70" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-56 p-1.5 shadow-xl border-slate-200/80"
              >
                {toolsLinks.map((t) => (
                  <DropdownMenuItem
                    key={t.href}
                    asChild
                    className="cursor-pointer rounded-md p-2 text-sm text-slate-700 hover:text-primary"
                  >
                    <Link to={t.href}>{t.title}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/testimonials"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                location.pathname === "/testimonials"
                  ? "text-primary font-semibold bg-primary/10"
                  : "text-slate-700 hover:text-primary hover:bg-slate-100 dark:text-slate-200"
              }`}
            >
              Testimonials
            </Link>

            <Link
              to="/blog"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                location.pathname === "/blog"
                  ? "text-primary font-semibold bg-primary/10"
                  : "text-slate-700 hover:text-primary hover:bg-slate-100 dark:text-slate-200"
              }`}
            >
              Articles
            </Link>

            <Link
              to="/contact"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                location.pathname === "/contact"
                  ? "text-primary font-semibold bg-primary/10"
                  : "text-slate-700 hover:text-primary hover:bg-slate-100 dark:text-slate-200"
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* Action CTAs */}
          <div className="hidden lg:flex items-center gap-2">
            <Button
              size="sm"
              onClick={onOpenConsultation}
              className="bg-primary hover:bg-primary/90 text-white shadow-md font-semibold text-xs px-4"
            >
              Free Consultation
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden items-center gap-2">
            <Button
              size="sm"
              onClick={onOpenConsultation}
              className="bg-primary hover:bg-primary/90 text-white text-xs px-3"
            >
              Consult
            </Button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-slate-700 hover:bg-slate-100 focus:outline-none"
              aria-label="Toggle Menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-x-0 top-[65px] bg-white dark:bg-slate-900 border-b border-slate-200 shadow-2xl p-6 max-h-[85vh] overflow-y-auto">
          <div className="flex flex-col gap-3">
            <Link
              to="/"
              className="text-base font-medium text-slate-900 py-1 border-b border-slate-100"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="text-base font-medium text-slate-900 py-1 border-b border-slate-100"
            >
              About Feingold
            </Link>

            <div className="pt-2">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                Our Services
              </div>
              <div className="grid grid-cols-1 gap-1 pl-2">
                {serviceLinks.map((s) => (
                  <Link
                    key={s.href}
                    to={s.href}
                    className="text-sm text-slate-700 py-1.5 hover:text-primary"
                  >
                    {s.title}
                  </Link>
                ))}
              </div>
            </div>

            <div className="pt-2">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                Calculators & Tools
              </div>
              <div className="grid grid-cols-1 gap-1 pl-2">
                {toolsLinks.map((t) => (
                  <Link
                    key={t.href}
                    to={t.href}
                    className="text-sm text-slate-700 py-1.5 hover:text-primary"
                  >
                    {t.title}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/pricing"
              className="text-base font-medium text-slate-900 py-1 border-b border-slate-100"
            >
              Pricing
            </Link>
            <Link
              to="/testimonials"
              className="text-base font-medium text-slate-900 py-1 border-b border-slate-100"
            >
              Testimonials
            </Link>
            <Link
              to="/blog"
              className="text-base font-medium text-slate-900 py-1 border-b border-slate-100"
            >
              Articles
            </Link>
            <Link
              to="/contact"
              className="text-base font-medium text-slate-900 py-1 border-b border-slate-100"
            >
              Contact Us
            </Link>

            <div className="pt-4 flex flex-col gap-2">
              <Button
                onClick={onOpenConsultation}
                className="w-full bg-primary text-white font-semibold"
              >
                Request Free Property Consultation
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
